/**
 * Canvas SIS-import `workflow_state` classification, shared by the SIS bundle
 * list (outcome chip + poll cadence) and the bundle detail stepper so the
 * buckets have exactly one source of truth.
 *
 * Mirrors the worker/uploads notion of terminal-ness but kept frontend-only and
 * keyed on the raw Canvas `workflow_state` string (lowercased).
 */

/** Clean, successful imports. */
export const CANVAS_OK = new Set([
  "imported",
  "imported_with_messages",
  "restored",
  "partially_restored",
]);
/** Canvas rejected or aborted the import. */
export const CANVAS_BAD = new Set([
  "failed",
  "failed_with_messages",
  "aborted",
]);
/** Canvas is still working. */
export const CANVAS_IN_FLIGHT = new Set(["created", "importing", "restoring"]);

export type CanvasOutcomeKind = "ok" | "warn" | "bad" | "in-flight" | "unknown";

/**
 * Bucket a Canvas `workflow_state` for display:
 *   ok        — clean import
 *   warn      — imported, but Canvas emitted messages (warnings)
 *   bad       — failed / aborted
 *   in-flight — still processing
 *   unknown   — absent or an unrecognized state
 */
export function canvasOutcomeKind(
  state: string | undefined,
): CanvasOutcomeKind {
  const cw = (state ?? "").toLowerCase();
  if (!cw) return "unknown";
  if (cw === "imported_with_messages" || cw === "partially_restored")
    return "warn";
  if (CANVAS_OK.has(cw)) return "ok";
  if (CANVAS_BAD.has(cw)) return "bad";
  if (CANVAS_IN_FLIGHT.has(cw)) return "in-flight";
  return "unknown";
}

/** True once Canvas reached a terminal `workflow_state` (ok or bad). */
export function canvasIsSettled(state: string | undefined): boolean {
  const cw = (state ?? "").toLowerCase();
  return CANVAS_OK.has(cw) || CANVAS_BAD.has(cw);
}
